import type { WorldSnapshot } from '~/data/types'
import type { EventBus } from '~/realtime/bus'

const REACTIONS = ['♥', '✦', '★', '♪', '✓', '♫']
const pick = <T,>(arr: T[], i: number): T => arr[i % arr.length]

/**
 * Gentle, plausible life over the REAL roster + pulses, so the office looks like
 * it's working even when the org is quiet. Choreography, not data: it never
 * invents people or rooms, and it only animates people who are actually AWAKE
 * (online humans + always-on agents) — away/offline people keep their real
 * resting state, so you never see a "sleeping" person walk around. Real Pusher
 * events ride on top; "Live only" mode turns this off entirely.
 */
export function startAmbient(snapshot: WorldSnapshot, bus: EventBus): () => void {
  const awake = new Set(
    snapshot.people.filter((p) => p.presence === 'online').map((p) => p.userId),
  )
  // Only rooms with at least one awake member can come to life.
  const rooms = snapshot.pulses
    .filter((p) => p.category === 'TEAM')
    .map((pulse) => ({
      pulse,
      active: pulse.memberUserIds.filter((id) => awake.has(id)),
    }))
    .filter((r) => r.active.length > 0)
  if (rooms.length === 0) return () => {}

  let tick = 0
  const timer = window.setInterval(() => {
    tick += 1
    const { pulse, active } = pick(rooms, tick * 3)
    const userId = pick(active, tick * 7)
    const roll = tick % 8

    if (roll <= 2) {
      // ambient: simulated chatter must never permanently relocate anyone.
      bus.emit({ kind: 'message', pulseId: pulse.id, userId, text: null, ambient: true })
    } else if (roll === 3) {
      bus.emit({
        kind: 'task',
        action: 'created',
        pulseId: pulse.id,
        taskId: `amb-${tick}`,
        assigneeUserIds: [userId],
        ambient: true,
      })
    } else if (roll === 4) {
      bus.emit({
        kind: 'task',
        action: 'updated',
        pulseId: pulse.id,
        taskId: `amb-${tick}`,
        assigneeUserIds: [userId],
        ambient: true,
      })
    } else if (roll === 5) {
      bus.emit({ kind: 'reaction', userId, symbol: pick(REACTIONS, tick), ambient: true })
    } else if (roll === 6) {
      bus.emit({ kind: 'insight', pulseId: pulse.id })
    } else {
      bus.emit({ kind: 'typing', pulseId: pulse.id, userId })
    }
  }, 2400)

  return () => window.clearInterval(timer)
}
